import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faTrash, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { getMessages, deleteMessage } from "../utils/firebase";
import "../sections/styles/MessagesPage.css";

const ADMIN_PASSWORD = process.env.REACT_APP_MESSAGES_PASSWORD || "admin";

const MessagesPage = () => {
	const [authed, setAuthed] = useState(false);
	const [password, setPassword] = useState("");
	const [error, setError] = useState(false);
	const [messages, setMessages] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const handleLogin = (e) => {
		e.preventDefault();
		if (password === ADMIN_PASSWORD) {
			setAuthed(true);
			setError(false);
			fetchMessages();
		} else {
			setError(true);
		}
	};

	const fetchMessages = async () => {
		setLoading(true);
		const data = await getMessages();
		setMessages(data);
		setLoading(false);
	};

	const handleDelete = async (id) => {
		await deleteMessage(id);
		setMessages(messages.filter((m) => m.id !== id));
	};

	const timeAgo = (timestamp) => {
		if (!timestamp) return "just now";
		const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
		const seconds = Math.floor((new Date() - date) / 1000);
		if (seconds < 60) return "just now";
		if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
		if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
		return `${Math.floor(seconds / 86400)}d ago`;
	};

	if (!authed) {
		return (
			<div className="page-spacer">
				<div className="page-grid-bg" />
				<div className="page-orb-1" />
				<div className="page-orb-2" />
				<div className="msg-login">
					<div className="msg-login__box">
						<FontAwesomeIcon icon={faLock} className="msg-login__icon" />
						<h2 className="msg-login__title">Messages</h2>
						<p className="msg-login__desc">This is private. Log in to continue.</p>
						<form onSubmit={handleLogin} className="msg-login__form">
							<input
								type="password"
								placeholder="Password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className={`msg-login__input ${error ? "msg-login__input--error" : ""}`}
								autoFocus
							/>
							<button type="submit" className="msg-login__btn">
								Enter
							</button>
						</form>
						{error && <p className="msg-login__error">Wrong password.</p>}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="page-spacer">
			<div className="page-grid-bg" />
			<div className="page-orb-1" />
			<div className="page-orb-2" />
			<div className="page-orb-3" />
			<section className="msg">
				<div className="content-wrapper">
					<div className="msg__header">
						<div>
							<h1 className="msg__title">Messages</h1>
							<p className="msg__count">
								{messages.length} message{messages.length !== 1 ? "s" : ""}
							</p>
						</div>
						<button onClick={fetchMessages} className="msg__refresh" disabled={loading}>
							{loading ? "Loading..." : "Refresh"}
						</button>
					</div>

					{messages.length === 0 && !loading && (
						<div className="msg__empty">
							<FontAwesomeIcon icon={faEnvelope} className="msg__empty-icon" />
							<p>No messages yet.</p>
						</div>
					)}

					<div className="msg__list">
						{messages.map((msg, i) => (
							<motion.div
								key={msg.id}
								className="msg__item"
								initial={{ opacity: 0, y: 15 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: i * 0.05 }}
							>
								<div className="msg__item-top">
									<div>
										<span className="msg__item-name">{msg.name}</span>
										<span className="msg__item-email">{msg.email}</span>
									</div>
									<div className="msg__item-actions">
										<span className="msg__item-time">{timeAgo(msg.created_at)}</span>
										<button
											className="msg__item-delete"
											onClick={() => handleDelete(msg.id)}
											title="Delete"
										>
											<FontAwesomeIcon icon={faTrash} />
										</button>
									</div>
								</div>
								<p className="msg__item-body">{msg.message}</p>
							</motion.div>
						))}
					</div>
				</div>
			</section>
		</div>
	);
};

export default MessagesPage;
